import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Repeat,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  X,
  CircleSlash,
  Trash2,
  Megaphone,
  Mic,
} from "lucide-react";
import AdminVoiceFillModal from "@/components/AdminVoiceFillModal";

const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };

function toISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  // Mon = 0 in our DAY_KEYS but JS getDay returns Sun=0
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function statusMeta(s) {
  switch (s) {
    case "completed":
      return { label: "Done", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 };
    case "missed":
      return { label: "Missed", cls: "bg-destructive/10 text-destructive", Icon: AlertTriangle };
    case "in_progress":
      return { label: "In progress", cls: "bg-amber-100 text-amber-800", Icon: Clock };
    case "cancelled":
      return { label: "Cancelled", cls: "bg-clinical-bg text-clinical-subtext", Icon: CircleSlash };
    default:
      return { label: "Scheduled", cls: "bg-clinical-primary/10 text-clinical-primary", Icon: Clock };
  }
}

export default function AdminSchedule() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [caregivers, setCaregivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [visits, setVisits] = useState([]);
  const [availability, setAvailability] = useState({}); // by user_id
  const [loading, setLoading] = useState(true);
  const [filterCaregiver, setFilterCaregiver] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [voiceScheduleOpen, setVoiceScheduleOpen] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  // Drag-and-drop reassignment state
  const [dragVisit, setDragVisit] = useState(null);          // visit object being dragged
  const [dropTarget, setDropTarget] = useState(null);        // {caregiver_id, date}
  const [dropping, setDropping] = useState(false);
  // Skill-match cache per client_id, populated lazily when a drag starts
  const [matchByClient, setMatchByClient] = useState({});

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const startISO = toISO(days[0]);
  const endISO = toISO(days[6]);

  const load = async () => {
    setLoading(true);
    try {
      const [cg, cl, vs, av] = await Promise.all([
        api.get("/auth/caregivers"),
        api.get("/clients"),
        api.get("/admin/visits", { params: { start: startISO, end: endISO } }),
        api.get("/admin/caregivers/availability"),
      ]);
      setCaregivers(cg.data || []);
      setClients(cl.data || []);
      setVisits(vs.data || []);
      const avMap = {};
      (av.data || []).forEach((r) => {
        avMap[r.user_id] = r.availability || {};
      });
      setAvailability(avMap);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startISO, endISO]);

  const visitsByCaregiverAndDay = useMemo(() => {
    const map = {};
    visits.forEach((v) => {
      const k = `${v.caregiver_id || "_unassigned"}_${v.date}`;
      if (!map[k]) map[k] = [];
      map[k].push(v);
    });
    return map;
  }, [visits]);

  const visibleCaregivers = useMemo(() => {
    if (filterCaregiver) return caregivers.filter((c) => c.id === filterCaregiver);
    return caregivers;
  }, [caregivers, filterCaregiver]);

  const isAvailableOn = (cgId, dateObj) => {
    const av = availability[cgId];
    if (!av || !av.weekly_pattern) return null; // unknown
    const dayKey = DAY_KEYS[(dateObj.getDay() + 6) % 7];
    const windows = (av.weekly_pattern[dayKey] || []).filter((w) => w.start && w.end);
    if (windows.length === 0) return false;
    // Time-off
    const iso = toISO(dateObj);
    const off = (av.time_off || []).some((t) => t.start_date && t.end_date && t.start_date <= iso && iso <= t.end_date);
    if (off) return false;
    return windows;
  };

  // ---- Drag-and-drop reassignment ----
  const fetchMatchesFor = async (clientId) => {
    if (matchByClient[clientId]) return matchByClient[clientId];
    try {
      const { data } = await api.get(`/admin/clients/${clientId}/match-caregivers`);
      const byId = {};
      for (const r of data.caregivers || []) byId[r.caregiver_id] = r;
      const cached = { byId, required_skills: data.required_skills, required_languages: data.required_languages };
      setMatchByClient((m) => ({ ...m, [clientId]: cached }));
      return cached;
    } catch {
      return null;
    }
  };

  const onDragStart = (visit, e) => {
    setDragVisit(visit);
    // Prefetch matches so the dropzone can colorize instantly
    if (visit.client_id) fetchMatchesFor(visit.client_id);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", visit.id);
    } catch (err) {
      // Older browser without dataTransfer — non-fatal, drag still fires
      console.debug("dataTransfer.setData unsupported:", err?.message);
    }
  };

  const onDragEnd = () => {
    setDragVisit(null);
    setDropTarget(null);
  };

  const onDragOver = (cgId, iso, e) => {
    if (!dragVisit) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (!dropTarget || dropTarget.caregiver_id !== cgId || dropTarget.date !== iso) {
      setDropTarget({ caregiver_id: cgId, date: iso });
    }
  };

  const dropMatchQuality = (cgId) => {
    // Returns 'ok' | 'warn' | 'block' for the current drag relative to the cell's caregiver.
    if (!dragVisit) return null;
    const cached = matchByClient[dragVisit.client_id];
    if (!cached) return "ok";
    const m = cached.byId?.[cgId];
    if (!m) return "ok";
    if (m.missing_required?.length || !m.has_language) return "block";
    if (m.score < 85) return "warn";
    return "ok";
  };

  const onDrop = async (cgId, iso, e) => {
    e.preventDefault();
    if (!dragVisit) return;
    const sameSlot = dragVisit.caregiver_id === cgId && dragVisit.date === iso;
    if (sameSlot) {
      onDragEnd();
      return;
    }
    const quality = dropMatchQuality(cgId);
    if (quality === "block") {
      const cached = matchByClient[dragVisit.client_id];
      const m = cached?.byId?.[cgId];
      const missing = m?.missing_required?.join(", ") || "required skills";
      if (!window.confirm(
        `This caregiver doesn't meet the client's care plan (missing: ${missing}).\n\nReassign anyway?`,
      )) {
        onDragEnd();
        return;
      }
    }
    setDropping(true);
    try {
      const payload = {
        client_id: dragVisit.client_id,
        caregiver_id: cgId || null,
        date: iso,
        start_time: dragVisit.start_time || null,
        end_time: dragVisit.end_time || null,
        status: dragVisit.status,
        notes: dragVisit.notes || "",
      };
      await api.put(`/admin/visits/${dragVisit.id}`, payload);
      toast.success("Visit moved");
      // Optimistic local update + reload
      setVisits((vs) =>
        vs.map((v) => (v.id === dragVisit.id ? { ...v, caregiver_id: cgId, date: iso } : v))
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not move visit");
    } finally {
      setDropping(false);
      onDragEnd();
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-schedule-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Operations</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            Schedule
          </h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Weekly view of caregiver visits and availability.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setShowRecurring(true)}
            className="btn-secondary"
            data-testid="recurring-visit-button"
          >
            <Repeat className="h-4 w-4" /> Recurring
          </button>
          <button
            type="button"
            onClick={() => setVoiceScheduleOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-xl bg-purple-100 px-3 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-200"
            data-testid="schedule-voice-button"
          >
            <Mic className="h-4 w-4" /> Voice
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="btn-primary"
            data-testid="new-visit-button"
          >
            <Plus className="h-4 w-4" /> New visit
          </button>
        </div>
      </div>

      {/* Week toolbar */}
      <div className="card-surface p-4 flex items-center justify-between flex-wrap gap-3" data-testid="schedule-toolbar">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setWeekStart(addDays(weekStart, -7))}
            className="btn-ghost !p-2"
            data-testid="prev-week-button"
            aria-label="Previous week"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-clinical-subtext" />
            <span className="font-medium text-clinical-text text-sm" data-testid="week-range">
              {days[0].toLocaleDateString([], { month: "short", day: "numeric" })} –{" "}
              {days[6].toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" })}
            </span>
          </div>
          <button
            type="button"
            onClick={() => setWeekStart(addDays(weekStart, 7))}
            className="btn-ghost !p-2"
            data-testid="next-week-button"
            aria-label="Next week"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => setWeekStart(startOfWeek(new Date()))}
            className="btn-ghost text-xs"
            data-testid="today-button"
          >
            Today
          </button>
        </div>
        <div>
          <select
            value={filterCaregiver}
            onChange={(e) => setFilterCaregiver(e.target.value)}
            className="input-clinical text-sm"
            data-testid="caregiver-filter"
          >
            <option value="">All caregivers ({caregivers.length})</option>
            {caregivers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-clinical-subtext">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading schedule…
        </div>
      ) : (
        <div className="card-surface overflow-x-auto" data-testid="schedule-grid">
          <table className="w-full min-w-[920px] text-sm">
            <thead className="sticky top-0 bg-white border-b border-clinical-border">
              <tr>
                <th className="text-left px-3 py-2.5 font-medium text-clinical-subtext text-xs uppercase tracking-wider w-44">
                  Caregiver
                </th>
                {days.map((d) => {
                  const isToday = toISO(d) === toISO(new Date());
                  return (
                    <th
                      key={d.toISOString()}
                      className={`text-center px-2 py-2.5 font-medium text-xs ${
                        isToday ? "text-clinical-primary" : "text-clinical-subtext"
                      }`}
                    >
                      <div className="uppercase tracking-wider">
                        {DAY_LABELS[DAY_KEYS[(d.getDay() + 6) % 7]]}
                      </div>
                      <div className={`text-base mt-0.5 ${isToday ? "font-semibold text-clinical-primary" : "text-clinical-text"}`}>
                        {d.getDate()}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {visibleCaregivers.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-clinical-subtext text-sm">
                    No caregivers to display.
                  </td>
                </tr>
              ) : (
                visibleCaregivers.map((cg) => (
                  <tr key={cg.id} className="border-t border-clinical-border" data-testid={`schedule-row-${cg.id}`}>
                    <td className="px-3 py-2 align-top">
                      <div className="font-medium text-clinical-text">{cg.name}</div>
                      <div className="text-[11px] text-clinical-subtext">{cg.email}</div>
                    </td>
                    {days.map((d) => {
                      const iso = toISO(d);
                      const cellVisits = visitsByCaregiverAndDay[`${cg.id}_${iso}`] || [];
                      const avail = isAvailableOn(cg.id, d);
                      const isOff = avail === false;
                      return (
                        <td
                          key={iso}
                          onDragOver={(e) => onDragOver(cg.id, iso, e)}
                          onDragLeave={() => {
                            if (dropTarget && dropTarget.caregiver_id === cg.id && dropTarget.date === iso) {
                              setDropTarget(null);
                            }
                          }}
                          onDrop={(e) => onDrop(cg.id, iso, e)}
                          className={`align-top px-1.5 py-1.5 border-l border-clinical-border min-w-[110px] transition-colors ${
                            isOff ? "bg-clinical-bg/60" : ""
                          } ${
                            dragVisit && dropTarget?.caregiver_id === cg.id && dropTarget?.date === iso
                              ? (dropMatchQuality(cg.id) === "block"
                                  ? "ring-2 ring-rose-400 ring-inset bg-rose-50/40"
                                  : dropMatchQuality(cg.id) === "warn"
                                    ? "ring-2 ring-amber-400 ring-inset bg-amber-50/40"
                                    : "ring-2 ring-emerald-400 ring-inset bg-emerald-50/40")
                              : ""
                          }`}
                          data-testid={`cell-${cg.id}-${iso}`}
                        >
                          {isOff && cellVisits.length === 0 ? (
                            <div className="text-[10px] text-clinical-subtext italic px-1 py-0.5">
                              Off
                            </div>
                          ) : null}
                          <div className="space-y-1">
                            {cellVisits.map((v) => {
                              const m = statusMeta(v.status);
                              const isDragging = dragVisit?.id === v.id;
                              return (
                                <button
                                  key={v.id}
                                  type="button"
                                  draggable
                                  onDragStart={(e) => onDragStart(v, e)}
                                  onDragEnd={onDragEnd}
                                  onClick={() => setEditingVisit(v)}
                                  className={`w-full text-left rounded-md px-2 py-1 text-[11px] transition-all hover:opacity-80 cursor-grab active:cursor-grabbing ${m.cls} ${isDragging ? "opacity-40" : ""}`}
                                  data-testid={`visit-${v.id}`}
                                >
                                  <div className="flex items-center gap-1 font-medium leading-tight">
                                    <m.Icon className="h-3 w-3 shrink-0" />
                                    <span className="truncate">
                                      {v.client_name || "—"}
                                    </span>
                                  </div>
                                  {(v.start_time || v.end_time) && (
                                    <div className="text-[10px] opacity-80 mt-0.5">
                                      {v.start_time || "—"}{v.end_time ? `–${v.end_time}` : ""}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                            <button
                              type="button"
                              onClick={() => {
                                setShowCreate(true);
                                window.__newVisitDefaults = { date: iso, caregiver_id: cg.id };
                              }}
                              className="w-full text-[10px] text-clinical-subtext rounded-md py-0.5 hover:bg-clinical-bg/60 transition-colors"
                              data-testid={`add-cell-${cg.id}-${iso}`}
                              aria-label={`Add visit ${iso}`}
                            >
                              + Add
                            </button>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Legend */}
      <div className="card-surface p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs" data-testid="schedule-legend">
        {[
          ["scheduled", "Scheduled"],
          ["in_progress", "In progress"],
          ["completed", "Done"],
          ["missed", "Missed"],
          ["cancelled", "Cancelled"],
        ].map(([k, label]) => {
          const m = statusMeta(k);
          return (
            <span key={k} className="flex items-center gap-1.5">
              <span className={`inline-flex h-4 w-4 items-center justify-center rounded ${m.cls}`}>
                <m.Icon className="h-2.5 w-2.5" />
              </span>
              {label}
            </span>
          );
        })}
        <span className="flex items-center gap-1.5 ml-auto text-clinical-subtext">
          <span className="inline-block h-4 w-4 rounded bg-clinical-bg/60 border border-clinical-border" />
          Caregiver off / unavailable
        </span>
      </div>

      <p className="text-[11px] text-clinical-subtext -mt-2 px-1" data-testid="schedule-dnd-hint">
        💡 Drag any visit chip onto another day or caregiver row to reassign — drop targets light up green/amber/red based on skill match.
      </p>

      {showCreate && (
        <VisitModal
          mode="create"
          clients={clients}
          caregivers={caregivers}
          defaults={typeof window !== "undefined" ? window.__newVisitDefaults : null}
          onClose={() => {
            if (typeof window !== "undefined") window.__newVisitDefaults = null;
            setShowCreate(false);
          }}
          onSaved={() => {
            if (typeof window !== "undefined") window.__newVisitDefaults = null;
            setShowCreate(false);
            load();
          }}
        />
      )}

      {editingVisit && (
        <VisitModal
          mode="edit"
          visit={editingVisit}
          clients={clients}
          caregivers={caregivers}
          onClose={() => setEditingVisit(null)}
          onSaved={() => {
            setEditingVisit(null);
            load();
          }}
        />
      )}

      {showRecurring && (
        <RecurringModal
          clients={clients}
          caregivers={caregivers}
          onClose={() => setShowRecurring(false)}
          onSaved={() => {
            setShowRecurring(false);
            load();
          }}
        />
      )}

      <AdminVoiceFillModal
        open={voiceScheduleOpen}
        onClose={() => setVoiceScheduleOpen(false)}
        endpoint="/ai/voice-fill/schedule"
        title="Voice schedule a visit"
        hint='e.g. "Schedule Sarah for Margaret tomorrow 9 to 1, with a morning bath."'
        examples={[
          "Schedule Sarah for Margaret tomorrow 9 to 1, with a morning bath.",
          "Book Maria with Henry next Monday 8am to 4pm.",
          "Cancel Sarah's Friday shift for Margaret.",
        ]}
        limitMs={45 * 1000}
        filenamePrefix="schedfill"
        testIdPrefix="voice-schedule"
        renderPreview={(r) => (
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label-eyebrow">Caregiver</div>
                <p className="text-clinical-text">
                  {r.caregiver_name || <span className="text-clinical-subtext italic">none</span>}
                  {r.matched_caregiver_id && <span className="ml-1 text-green-700">✓</span>}
                </p>
              </div>
              <div>
                <div className="label-eyebrow">Client</div>
                <p className="text-clinical-text">
                  {r.client_name || <span className="text-clinical-subtext italic">none</span>}
                  {r.matched_client_id && <span className="ml-1 text-green-700">✓</span>}
                </p>
              </div>
              <div>
                <div className="label-eyebrow">Date</div>
                <p className="text-clinical-text">{r.resolved_date || r.date_phrase || "—"}</p>
              </div>
              <div>
                <div className="label-eyebrow">Time</div>
                <p className="text-clinical-text">
                  {r.start_time || "—"} – {r.end_time || "—"}
                </p>
              </div>
            </div>
            {r.notes && (
              <div>
                <div className="label-eyebrow">Visit notes</div>
                <p className="text-clinical-text">{r.notes}</p>
              </div>
            )}
            <div className="text-xs text-clinical-subtext">
              Confirm to open the visit editor pre-filled with these values.
            </div>
          </div>
        )}
        onConfirm={(r) => {
          if (typeof window !== "undefined") {
            window.__newVisitDefaults = {
              caregiver_id: r.matched_caregiver_id || "",
              client_id: r.matched_client_id || "",
              date: r.resolved_date || "",
              start_time: r.start_time || "",
              end_time: r.end_time || "",
              notes: r.notes || "",
            };
          }
          setVoiceScheduleOpen(false);
          setShowCreate(true);
        }}
        confirmLabel="Open editor"
      />
    </div>
  );
}

function ConflictBanner({ warnings }) {
  if (!warnings || warnings.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-300/40 bg-amber-50 p-3 space-y-1.5" data-testid="conflict-banner">
      {warnings.map((w, i) => (
        <div
          key={`warn-${w.kind || "x"}-${i}-${(w.message || "").slice(0, 20)}`}
          className={`flex items-start gap-2 text-xs ${
            w.severity === "error" ? "text-destructive" : "text-amber-800"
          }`}
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{w.message}</span>
        </div>
      ))}
    </div>
  );
}

function VisitModal({ mode, visit, clients, caregivers, defaults, onClose, onSaved }) {
  const [form, setForm] = useState({
    client_id: visit?.client_id || defaults?.client_id || "",
    caregiver_id: visit?.caregiver_id || defaults?.caregiver_id || "",
    date: visit?.date || defaults?.date || new Date().toISOString().slice(0, 10),
    start_time: visit?.start_time || defaults?.start_time || "",
    end_time: visit?.end_time || defaults?.end_time || "",
    notes: visit?.notes || defaults?.notes || "",
    status: visit?.status || "scheduled",
  });
  const [warnings, setWarnings] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [matches, setMatches] = useState(null);  // {caregivers: [...]}
  const [broadcasting, setBroadcasting] = useState(false);

  // Pull match data when client changes (admin sees match-% next to each caregiver name)
  useEffect(() => {
    if (!form.client_id) {
      setMatches(null);
      return;
    }
    api
      .get(`/admin/clients/${form.client_id}/match-caregivers`)
      .then(({ data }) => setMatches(data))
      .catch(() => setMatches(null));
  }, [form.client_id]);

  const matchByCgId = useMemo(() => {
    const m = {};
    for (const r of matches?.caregivers || []) m[r.caregiver_id] = r;
    return m;
  }, [matches]);

  useEffect(() => {
    if (!form.client_id || !form.caregiver_id || !form.date) {
      setWarnings([]);
      return;
    }
    setChecking(true);
    const t = setTimeout(() => {
      api
        .post("/admin/visits/check-conflicts", {
          client_id: form.client_id,
          caregiver_id: form.caregiver_id,
          date: form.date,
          start_time: form.start_time,
          end_time: form.end_time,
        })
        .then(({ data }) => {
          // If editing, ignore the double-book that points at this same visit time
          const w = (data.warnings || []).filter((x) => {
            if (mode !== "edit" || x.kind !== "double_book") return true;
            return !x.message.includes(visit?.client_name || "");
          });
          setWarnings(w);
        })
        .catch(() => setWarnings([]))
        .finally(() => setChecking(false));
    }, 400);
    return () => clearTimeout(t);
  }, [form.client_id, form.caregiver_id, form.date, form.start_time, form.end_time, mode, visit?.client_name]);

  const save = async () => {
    if (!form.client_id) {
      toast.error("Pick a client");
      return;
    }
    if (!form.date) {
      toast.error("Pick a date");
      return;
    }
    setSaving(true);
    try {
      if (mode === "create") {
        await api.post("/admin/visits", form);
        toast.success("Visit created");
      } else {
        await api.put(`/admin/visits/${visit.id}`, form);
        toast.success("Visit updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save visit");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this visit? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/visits/${visit.id}`);
      toast.success("Visit deleted");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const broadcast = async () => {
    if (!visit?.id) return;
    if (!window.confirm("Email this open shift to every qualified caregiver?")) return;
    setBroadcasting(true);
    try {
      const { data } = await api.post(`/admin/visits/${visit.id}/broadcast`);
      if (data.skipped) {
        toast.warning(data.reason || "No qualified caregivers");
      } else {
        toast.success(`Open shift emailed to ${data.sent} caregiver${data.sent === 1 ? "" : "s"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="visit-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <h3 className="font-heading text-lg font-medium">
            {mode === "create" ? "New visit" : "Edit visit"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-ghost p-1.5"
            data-testid="visit-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="label-eyebrow mb-1.5 block">Client</label>
            <select
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="input-clinical"
              data-testid="visit-client-select"
              disabled={mode === "edit"}
            >
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Caregiver</label>
            <select
              value={form.caregiver_id || ""}
              onChange={(e) => setForm({ ...form, caregiver_id: e.target.value || null })}
              className="input-clinical"
              data-testid="visit-caregiver-select"
            >
              <option value="">Unassigned</option>
              {caregivers.map((c) => {
                const m = matchByCgId[c.id];
                const tag = m
                  ? (m.missing_required?.length ? ` — ${m.score}% (gap: ${m.missing_required.join(", ")})` : ` — ${m.score}% match`)
                  : "";
                return (
                  <option key={c.id} value={c.id}>{c.name}{tag}</option>
                );
              })}
            </select>
            {matches && (matches.required_skills?.length || matches.required_languages?.length) ? (
              <p className="text-[11px] text-clinical-subtext mt-1" data-testid="visit-match-summary">
                Client requires: {[...(matches.required_skills || []), ...(matches.required_languages || [])].join(", ")}.
                Pick a caregiver with ≥60% match for full coverage.
              </p>
            ) : null}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Date</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="input-clinical"
                data-testid="visit-date-input"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Start</label>
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="input-clinical"
                data-testid="visit-start-input"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">End</label>
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="input-clinical"
                data-testid="visit-end-input"
              />
            </div>
          </div>

          {mode === "edit" && (
            <div>
              <label className="label-eyebrow mb-1.5 block">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="input-clinical"
                data-testid="visit-status-select"
              >
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In progress</option>
                <option value="completed">Completed</option>
                <option value="missed">Missed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}

          <div>
            <label className="text-xs text-clinical-subtext block mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="input-clinical resize-y"
              data-testid="visit-notes-input"
            />
          </div>

          {checking && (
            <div className="text-[11px] text-clinical-subtext flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Checking for conflicts…
            </div>
          )}
          <ConflictBanner warnings={warnings} />
        </div>

        <div className="sticky bottom-0 bg-white flex items-center justify-between gap-2 px-5 py-4 border-t border-clinical-border">
          {mode === "edit" ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={remove}
                disabled={saving}
                className="btn-ghost !text-destructive"
                data-testid="visit-delete"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
              {!visit?.caregiver_id && (
                <button
                  type="button"
                  onClick={broadcast}
                  disabled={broadcasting || saving}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-clinical-border bg-white px-3 py-1.5 text-sm font-medium hover:bg-clinical-bg"
                  data-testid="visit-broadcast"
                  title="Email this open shift to qualified caregivers"
                >
                  {broadcasting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Megaphone className="h-3.5 w-3.5" />}
                  Broadcast
                </button>
              )}
            </div>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="btn-secondary"
              data-testid="visit-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="btn-primary"
              data-testid="visit-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "create" ? "Create visit" : "Save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecurringModal({ clients, caregivers, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const fourWeeks = new Date();
  fourWeeks.setDate(fourWeeks.getDate() + 28);
  const [form, setForm] = useState({
    client_id: "",
    caregiver_id: "",
    days_of_week: [],
    start_time: "09:00",
    end_time: "13:00",
    start_date: today,
    end_date: fourWeeks.toISOString().slice(0, 10),
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const toggleDay = (key) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(key)
        ? f.days_of_week.filter((d) => d !== key)
        : [...f.days_of_week, key],
    }));
  };

  const save = async () => {
    if (!form.client_id) {
      toast.error("Pick a client");
      return;
    }
    if (form.days_of_week.length === 0) {
      toast.error("Pick at least one day of week");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/visits/recurring", {
        ...form,
        caregiver_id: form.caregiver_id || null,
      });
      const conflictCount = (data.conflicts || []).length;
      if (conflictCount > 0) {
        toast.warning(
          `Created ${data.created} visit${data.created === 1 ? "" : "s"} — ${conflictCount} have conflicts (check the grid)`,
          { duration: 5000 }
        );
      } else {
        toast.success(`Created ${data.created} visit${data.created === 1 ? "" : "s"}`);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create recurring visits");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4"
      onClick={onClose}
      data-testid="recurring-modal"
    >
      <div
        className="bg-white sm:rounded-2xl border-t sm:border border-clinical-border w-full sm:max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white flex items-center justify-between px-5 py-4 border-b border-clinical-border">
          <div>
            <h3 className="font-heading text-lg font-medium">Recurring visits</h3>
            <p className="text-[11px] text-clinical-subtext">
              Bulk-create visits across a date range on selected weekdays.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-ghost p-1.5"
            data-testid="recurring-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="label-eyebrow mb-1.5 block">Client</label>
            <select
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              className="input-clinical"
              data-testid="recurring-client-select"
            >
              <option value="">Select a client…</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Caregiver</label>
            <select
              value={form.caregiver_id}
              onChange={(e) => setForm({ ...form, caregiver_id: e.target.value })}
              className="input-clinical"
              data-testid="recurring-caregiver-select"
            >
              <option value="">Unassigned</option>
              {caregivers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Days of week</label>
            <div className="flex flex-wrap gap-1.5" data-testid="recurring-days-picker">
              {DAY_KEYS.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => toggleDay(k)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    form.days_of_week.includes(k)
                      ? "bg-clinical-primary text-white border-clinical-primary"
                      : "bg-white text-clinical-text border-clinical-border hover:bg-clinical-bg"
                  }`}
                  data-testid={`recurring-day-${k}`}
                >
                  {DAY_LABELS[k]}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow mb-1.5 block">Start time</label>
              <input
                type="time"
                value={form.start_time}
                onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                className="input-clinical"
                data-testid="recurring-start-time"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">End time</label>
              <input
                type="time"
                value={form.end_time}
                onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                className="input-clinical"
                data-testid="recurring-end-time"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">Start date</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="input-clinical"
                data-testid="recurring-start-date"
              />
            </div>
            <div>
              <label className="label-eyebrow mb-1.5 block">End date</label>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                className="input-clinical"
                data-testid="recurring-end-date"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-clinical-subtext block mb-1">Notes</label>
            <input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-clinical"
              data-testid="recurring-notes-input"
            />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white flex items-center justify-end gap-2 px-5 py-4 border-t border-clinical-border">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="btn-secondary"
            data-testid="recurring-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="btn-primary"
            data-testid="recurring-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create visits"}
          </button>
        </div>
      </div>
    </div>
  );
}
